/**
 * HipPatientRecordsPage - /org/health-info/publish/patient/:patientId (M2, ABDM HIP, OR-75)
 *
 * Every visit and admission of ONE patient for the last 12 months, as the same checklist cards as
 * the main list. A PAGE (not a drawer): the cards open their own drawers, the list can be long, and
 * Back / a shared address work.
 *
 *   - The visit list is HipRecordsList in patient mode (`patientId`, build-plan-redesign R-F9). It owns the
 *     ONE request (GET /hip/records with `patient_id`) and hands the `patient` block of the answer to this
 *     page through `onPatient`; the header (name, UHID, masked mobile, ABHA status) is drawn from that.
 *     No ABHA on file -> the header and a plain note, and the list is taken away. Unknown patient
 *     (`onError`, PATIENT_NOT_FOUND) -> not found. Any other failure is the list's own error + Retry.
 *   - "<- Back to the list": the list keeps its filters in its address (search params), so going back
 *     in history restores them exactly. Opened with no history (direct link / reload) -> the list route.
 *
 * Requests: none of its own. ONE per open (the list's, keyed on the primitive `patientId`; the inner
 * component is keyed by it so another patient starts clean). The whole page sits behind AbdmEnabledGate
 * (ABDM off = notice, the list is never mounted, no request).
 */
import React, { useCallback, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import AbdmEnabledGate from '../components/common/AbdmEnabledGate';
import HipRecordsList from '../components/hip/records/HipRecordsList';
import { usePermission } from '../context/PermissionContext';
import { useTheme } from '../theme/ThemeContext';
import { ROUTES } from '../constants/routes';
import { ABHA_STATUS } from '../constants/hipRecordEnums';
import { ABHA_VERIFIED, ABHA_NOT_VERIFIED, CARD, PATIENT } from '../constants/hipRecordsCopy';

const PATIENT_NOT_FOUND = 'PATIENT_NOT_FOUND'; // 404 detail.code (build-plan §4.7 A4.3)

// react-router gives the first entry of a session the key 'default': nothing to go back to inside the app.
const FIRST_ENTRY_KEY = 'default';

const CHIP = 'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold tracking-wide whitespace-nowrap';
const CHIP_TONE = {
  [ABHA_STATUS.VERIFIED]: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  [ABHA_STATUS.NOT_VERIFIED]: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};
const NOTE = 'py-8 text-center text-sm text-slate-700 dark:text-slate-200';
const BTN = 'rounded-md border px-3 py-1 text-xs font-semibold whitespace-nowrap';
const BTN_NEUTRAL = `${BTN} border-slate-400 bg-slate-100 text-slate-800 hover:bg-slate-200 dark:border-slate-500 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600`;
const BTN_TEAL = `${BTN} border-teal-500 bg-teal-50 text-teal-700 hover:bg-teal-100 dark:border-teal-400 dark:bg-teal-900 dark:text-teal-300 dark:hover:bg-teal-800`;

const initialsOf = (name) => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
};

function PatientHeader({ patient }) {
  const navigate = useNavigate();
  const canVerifyAbha = usePermission('ABHA_VERIFY');
  const notVerified = patient.abhaStatus === ABHA_STATUS.NOT_VERIFIED;

  return (
    <section className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-slate-300 bg-white p-4 dark:border-slate-600 dark:bg-slate-800">
      <span
        aria-hidden="true"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-100 text-sm font-extrabold text-teal-800 dark:bg-teal-900 dark:text-teal-200"
      >
        {initialsOf(patient.patientName)}
      </span>
      <div className="min-w-0 flex-1">
        <h1 className="m-0 text-xl font-bold text-slate-900 [overflow-wrap:anywhere] dark:text-slate-100">{patient.patientName}</h1>
        <p className="m-0 mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          {PATIENT.headerLine(patient.uhid, patient.patientMobileMasked)}
        </p>
      </div>
      {patient.abhaStatus && (
        <span className={`${CHIP} ${CHIP_TONE[patient.abhaStatus]}`}>
          {notVerified ? ABHA_NOT_VERIFIED : ABHA_VERIFIED}
        </span>
      )}
      {!patient.abhaStatus && (
        <p role="status" className="m-0 basis-full text-sm text-slate-700 dark:text-slate-200">{PATIENT.noAbha}</p>
      )}
      {notVerified && (
        <>
          <p className="m-0 basis-full text-sm text-slate-700 dark:text-slate-200">{CARD.notVerifiedHint}</p>
          {canVerifyAbha && (
            <button type="button" className={BTN_TEAL} onClick={() => navigate(ROUTES.ABHA)}>{CARD.verifyAbha}</button>
          )}
        </>
      )}
    </section>
  );
}

/** The header + the list of ONE patient. Keyed by patientId (see the Body), so another patient starts clean. */
function PatientRecords({ patientId }) {
  const [patient, setPatient] = useState(null); // the `patient` block of the list's answer
  const [notFound, setNotFound] = useState(false);
  const onError = useCallback((err) => { if (err && err.code === PATIENT_NOT_FOUND) setNotFound(true); }, []);

  if (notFound) return <p role="alert" className={NOTE}>{PATIENT.notFound}</p>;
  return (
    <>
      {patient && <PatientHeader patient={patient} />}
      {/* Nothing to link without an ABHA: the answer is in, so the list goes (it makes no further request). */}
      {!(patient && !patient.abhaStatus) && (
        <HipRecordsList patientId={patientId} onPatient={setPatient} onError={onError} />
      )}
    </>
  );
}

function HipPatientRecordsPageBody() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  const hasHistory = location.key !== FIRST_ENTRY_KEY;
  const goBack = () => (hasHistory ? navigate(-1) : navigate(ROUTES.HIP_CARE_CONTEXTS));
  // Optional: the link that opened this page may hand over the list's filter words (router state).
  const listFilters = location.state && Array.isArray(location.state.listFilters) ? location.state.listFilters : [];

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  return (
    <div style={{ minHeight: '100vh', background: bg }}>
      <AdminPageHeader />

      <main style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <button type="button" className={BTN_NEUTRAL} onClick={goBack}>{PATIENT.back}</button>
          {hasHistory && listFilters.length > 0 && (
            <span className="text-xs text-slate-600 dark:text-slate-300">{PATIENT.filtersKept(listFilters.join(' · '))}</span>
          )}
        </div>

        <PatientRecords key={patientId} patientId={patientId} />
      </main>
    </div>
  );
}

export default function HipPatientRecordsPage() {
  return (
    <AbdmEnabledGate>
      <HipPatientRecordsPageBody />
    </AbdmEnabledGate>
  );
}
